import React, { useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  LinearProgress,
  CircularProgress
} from "@mui/material";
import {
  UploadCloud,
  Eye,
  Trash2,
  FileText
} from "lucide-react";

const MaterialTabPanel = ({
  category,
  title,
  formatHint,
  acceptFormats,
  files = [],
  onUpload,
  onRemove,
  onPreview,
  userRole = "designer",
  isReadOnly = false
}) => {
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    onUpload(e, category);
  };

  return (
    <Box sx={{ pt: 1 }}>
      {/* Upload Zone for Designer (Only when not read-only) */}
      {userRole !== "admin" && !isReadOnly && (
        <Box sx={{ mb: 2.5 }}>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept={acceptFormats}
            style={{ display: "none" }}
            onChange={handleInputChange}
          />
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              p: 2.5,
              borderRadius: "10px",
              border: "1.5px dashed #bfdbfe",
              backgroundColor: "#f8faff",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#1877f2",
                backgroundColor: "#eff6ff"
              }
            }}
          >
            <UploadCloud
              size={26}
              color="#1877f2"
              style={{ margin: "0 auto 6px" }}
            />
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "#1877f2"
              }}
            >
              Click to upload multiple {title.toLowerCase()} or drag and drop
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#94a3b8", fontSize: "0.7rem" }}
            >
              Supported: {formatHint}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Uploaded Files Section (Only rendered when files exist) */}
      {files.length > 0 ? (
        <Box sx={{ mt: userRole !== "admin" && !isReadOnly ? 2 : 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(280px, 1fr))"
              },
              gap: 1.5,
              maxHeight: "360px",
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "4px"
              }
            }}
          >
            {files.map((file) => (
              <Paper
                key={file.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "#cbd5e1",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.04)"
                  }
                }}
              >
                {/* Top Details & Thumbnail */}
                <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                  <Box
                    onClick={() => !file.isUploading && onPreview(file)}
                    sx={{
                      width: 72,
                      height: 56,
                      borderRadius: "6px",
                      overflow: "hidden",
                      backgroundColor: "#e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: file.isUploading ? "default" : "pointer",
                      flexShrink: 0,
                      border: "1px solid #cbd5e1"
                    }}
                  >
                    {(() => {
                      const isImage =
                        (file.type && file.type.startsWith("image/")) ||
                        /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(file.name || "");
                      if (file.isUploading) {
                        return <CircularProgress size={22} sx={{ color: "#1877f2" }} />;
                      }
                      if (file.url && isImage) {
                        return (
                          <img
                            src={file.url}
                            alt={file.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover"
                            }}
                          />
                        );
                      }
                      return <FileText size={26} color="#dc2626" />;
                    })()}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25, flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {file.name}
                      </Typography>
                      {file.isNew && (
                        <Chip
                          label="New"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.625rem",
                            fontWeight: 700,
                            backgroundColor: "#eff6ff",
                            color: "#1877f2",
                            border: "1px solid #bfdbfe",
                            borderRadius: "4px"
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: file.isUploading ? "#2563eb" : "#94a3b8",
                        fontSize: "0.6875rem",
                        display: "block",
                        fontWeight: file.isUploading ? 600 : 400
                      }}
                    >
                      {file.isUploading
                        ? `Uploading... ${file.uploadProgress || 0}%`
                        : `${file.size || ""} ${file.uploadedAt ? `• ${file.uploadedAt}` : ""}`}
                    </Typography>

                    {file.isUploading && (
                      <LinearProgress
                        variant="determinate"
                        value={file.uploadProgress || 0}
                        sx={{ height: 4, borderRadius: 2, mt: 0.75 }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Action Buttons Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 1,
                    borderTop: "1px solid #f1f5f9"
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={Boolean(file.isUploading)}
                    startIcon={<Eye size={13} />}
                    onClick={() => onPreview(file)}
                    sx={{
                      py: 0.3,
                      px: 1.25,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      borderRadius: "6px",
                      textTransform: "none",
                      color: "#1877f2",
                      borderColor: "#bfdbfe",
                      backgroundColor: "#eff6ff",
                      "&:hover": {
                        backgroundColor: "#dbeafe",
                        borderColor: "#1877f2"
                      }
                    }}
                  >
                    {userRole === "admin" ? "View Material" : "Preview"}
                  </Button>

                  {/* Designer Action: Delete (Only when not read-only) */}
                  {userRole !== "admin" && !isReadOnly && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={Boolean(file.isUploading)}
                      startIcon={<Trash2 size={13} />}
                      onClick={(e) => onRemove(category, file.id, e)}
                      sx={{
                        py: 0.3,
                        px: 1.25,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        borderRadius: "6px",
                        textTransform: "none",
                        borderColor: "#fecaca",
                        backgroundColor: "#fef2f2",
                        "&:hover": {
                          backgroundColor: "#fee2e2",
                          borderColor: "#ef4444"
                        }
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      ) : isReadOnly ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.8125rem" }}>
            No {title.toLowerCase()} uploaded for this event.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default MaterialTabPanel;
